import { Head } from "@inertiajs/react"
import {
  MapPin,
  Leaf,
  Factory,
  FlaskConical,
  Mountain,
  Calendar,
  Coffee as CoffeeIcon,
} from "lucide-react"

import type {
  VisibilityToggles,
  AIContent,
  CustomContent,
  PublishedCoffeeRecipe,
} from "@/types"
import { ScoreRadarChart } from "@/components/charts/ScoreRadarChart"

interface Sample {
  id: number
  name: string
  origin: string | null
  land: string | null
  producer: string | null
  variety: string | null
  process: string | null
  description: string | null
}

interface Scores {
  fragrance_score: number | null
  acidity_score: number | null
  body_score: number | null
  flavor_score: number | null
  aftertaste_score: number | null
  balance_score: number | null
  overall_score: number | null
  uniformity_score: number | null
  clean_cup_score: number | null
  sweetness_score: number | null
  aroma_rating: number | null
  aroma_sca_selections: Record<string, string[]> | null
  roast_level: string | null
  total_score: number | null
}

interface Organization {
  name: string
  slug: string
  logo_url: string | null
  primary_color: string | null
  secondary_color: string | null
  website_url: string | null
  instagram_handle: string | null
}

interface CoffeeData {
  name: string
  slug: string
  ai_content: AIContent | null
  custom_content: CustomContent | null
  visibility_toggles: VisibilityToggles
  recipes: PublishedCoffeeRecipe[]
}

interface Props {
  coffee: CoffeeData
  sample: Sample | null
  scores: Scores | null
  cupping_form: string | null
  organization: Organization | null
}

function gradeFromScore(score: number): { grade: string; color: string } {
  if (score >= 90) return { grade: "Outstanding", color: "text-emerald-600" }
  if (score >= 85) return { grade: "Excellent", color: "text-green-600" }
  if (score >= 80) return { grade: "Very Good", color: "text-blue-600" }
  if (score >= 75) return { grade: "Good", color: "text-yellow-600" }
  return { grade: "Below Specialty", color: "text-gray-600" }
}

const methodLabels: Record<string, string> = {
  espresso: "Espresso",
  french_press: "French Press",
  auto_drip: "Auto Drip",
  v60: "V60 / Conic Pour Over",
  kalita_wave: "Kalita Wave / Flat Pour Over",
  elf_dripper: "Elf Dripper",
  origami: "Origami",
  eggonaut: "Eggonaut",
  meteorite_dripper: "Meteorite Dripper",
}

export default function PublicCoffee({
  coffee,
  sample,
  scores,
  cupping_form,
  organization,
}: Props) {
  const toggles = coffee.visibility_toggles || ({} as VisibilityToggles)
  const protocol = cupping_form?.includes("cva") ? "cva" : "sca"
  const primaryColor = organization?.primary_color || "#6F4E37"
  const ai = coffee.ai_content
  const custom = coffee.custom_content

  return (
    <>
      <Head title={`${coffee.name} | ${organization?.name || "Catador Pro"}`} />

      <div className="min-h-screen bg-white dark:bg-neutral-950">
        {/* Header */}
        <header className="border-b px-4 py-4 sm:px-6">
          <div className="mx-auto flex max-w-4xl items-center justify-between">
            <div className="flex items-center gap-3">
              {organization?.logo_url ? (
                <img
                  src={organization.logo_url}
                  alt={organization.name}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  {organization?.name?.charAt(0) || "C"}
                </div>
              )}
              <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-200">
                {organization?.name}
              </span>
            </div>
            {organization?.website_url && (
              <a
                href={organization.website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-neutral-500 hover:text-neutral-700 dark:text-neutral-400"
              >
                {organization.website_url.replace(/^https?:\/\//, "")}
              </a>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6">
          {/* Hero Section */}
          <section className="mb-10 text-center">
            <h1 className="mb-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl dark:text-neutral-100">
              {coffee.name}
            </h1>
            {custom?.tagline && (
              <p className="mb-3 text-lg text-neutral-500 dark:text-neutral-400">
                {custom.tagline}
              </p>
            )}
            {ai?.description && (
              <p className="mx-auto max-w-2xl text-neutral-600 dark:text-neutral-300">
                {ai.description}
              </p>
            )}

            {/* Score Badge */}
            {toggles.show_scores && scores?.total_score && (
              <div className="mt-6 inline-flex flex-col items-center">
                <div
                  className="flex h-24 w-24 items-center justify-center rounded-full border-4 text-white"
                  style={{
                    borderColor: primaryColor,
                    backgroundColor: primaryColor,
                  }}
                >
                  <span className="text-2xl font-bold">
                    {scores.total_score.toFixed(1)}
                  </span>
                </div>
                <p
                  className={`mt-2 text-sm font-medium ${gradeFromScore(scores.total_score).color}`}
                >
                  {gradeFromScore(scores.total_score).grade}
                </p>
              </div>
            )}
          </section>

          <div className="grid gap-8 lg:grid-cols-3">
            {/* Main Content */}
            <div className="space-y-8 lg:col-span-2">
              {/* Tasting Notes */}
              {toggles.show_tasting_notes && ai?.consumer_notes && (
                <section>
                  <h2 className="mb-3 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    Tasting Notes
                  </h2>
                  <p className="leading-relaxed text-neutral-600 dark:text-neutral-300">
                    {ai.consumer_notes}
                  </p>
                  {ai.flavor_tags && ai.flavor_tags.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {ai.flavor_tags.map((tag, i) => (
                        <span
                          key={i}
                          className="rounded-full px-3 py-1 text-sm font-medium text-white"
                          style={{ backgroundColor: primaryColor }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </section>
              )}

              {/* Story */}
              {custom?.story && (
                <section>
                  <h2 className="mb-3 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    The Story
                  </h2>
                  <p className="leading-relaxed text-neutral-600 dark:text-neutral-300">
                    {custom.story}
                  </p>
                </section>
              )}

              {/* Sensory Profile Chart */}
              {toggles.show_scores && scores && (
                <section>
                  <h2 className="mb-4 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    Sensory Profile
                  </h2>
                  <div className="rounded-lg border bg-neutral-50 p-4 dark:bg-neutral-900">
                    <div className="h-[280px] w-full">
                      <ScoreRadarChart
                        protocol={protocol}
                        scores={{
                          fragrance_score: scores.fragrance_score || 0,
                          aroma_rating: scores.aroma_rating || undefined,
                          flavor_score: scores.flavor_score || 0,
                          aftertaste_score: scores.aftertaste_score || 0,
                          acidity_score: scores.acidity_score || 0,
                          sweetness_score: scores.sweetness_score || 0,
                          body_score: scores.body_score || 0,
                          overall_score: scores.overall_score || 0,
                          balance_score: scores.balance_score || undefined,
                        }}
                      />
                    </div>
                  </div>
                </section>
              )}

              {/* Brew Recipes */}
              {toggles.show_recipes && coffee.recipes.length > 0 && (
                <section>
                  <h2 className="mb-4 text-xl font-semibold text-neutral-900 dark:text-neutral-100">
                    Recommended Recipes
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {coffee.recipes.map((recipe) => (
                      <div key={recipe.id} className="rounded-lg border p-4">
                        <div className="mb-3 flex items-center gap-2">
                          <CoffeeIcon
                            className="h-4 w-4"
                            style={{ color: primaryColor }}
                          />
                          <h3 className="font-semibold capitalize">
                            {methodLabels[recipe.method] ||
                              recipe.method.replace(/_/g, " ")}
                          </h3>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                          {recipe.dose && (
                            <div>
                              <span className="text-neutral-500">Dose</span>
                              <p className="font-medium">{recipe.dose}g</p>
                            </div>
                          )}
                          {recipe.yield_amount && (
                            <div>
                              <span className="text-neutral-500">Yield</span>
                              <p className="font-medium">
                                {recipe.yield_amount}ml
                              </p>
                            </div>
                          )}
                          {recipe.ratio && (
                            <div>
                              <span className="text-neutral-500">Ratio</span>
                              <p className="font-medium">{recipe.ratio}</p>
                            </div>
                          )}
                          {recipe.temp && (
                            <div>
                              <span className="text-neutral-500">
                                Temperature
                              </span>
                              <p className="font-medium">{recipe.temp}°C</p>
                            </div>
                          )}
                          {recipe.time && (
                            <div>
                              <span className="text-neutral-500">Time</span>
                              <p className="font-medium">{recipe.time}</p>
                            </div>
                          )}
                          {recipe.grind && (
                            <div>
                              <span className="text-neutral-500">Grind</span>
                              <p className="font-medium">{recipe.grind}</p>
                            </div>
                          )}
                        </div>
                        {recipe.ai_tip && (
                          <p className="mt-3 border-t pt-3 text-sm text-neutral-500 italic dark:text-neutral-400">
                            {recipe.ai_tip}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Origin Card */}
              {toggles.show_origin && sample && (
                <section className="rounded-lg border p-5">
                  <h2 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    Origin
                  </h2>
                  <dl className="space-y-3">
                    {sample.origin && (
                      <div className="flex items-start gap-3">
                        <MapPin className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Country</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {sample.origin}
                          </dd>
                        </div>
                      </div>
                    )}
                    {sample.land && (
                      <div className="flex items-start gap-3">
                        <Mountain className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Region</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {sample.land}
                          </dd>
                        </div>
                      </div>
                    )}
                    {toggles.show_producer && sample.producer && (
                      <div className="flex items-start gap-3">
                        <Factory className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Producer</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {sample.producer}
                          </dd>
                        </div>
                      </div>
                    )}
                    {sample.variety && (
                      <div className="flex items-start gap-3">
                        <Leaf className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Variety</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {sample.variety}
                          </dd>
                        </div>
                      </div>
                    )}
                    {sample.process && (
                      <div className="flex items-start gap-3">
                        <FlaskConical className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Process</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {sample.process}
                          </dd>
                        </div>
                      </div>
                    )}
                    {custom?.altitude && (
                      <div className="flex items-start gap-3">
                        <Mountain className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Altitude</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {custom.altitude}
                          </dd>
                        </div>
                      </div>
                    )}
                    {custom?.harvest && (
                      <div className="flex items-start gap-3">
                        <Calendar className="mt-0.5 h-4 w-4 text-neutral-400" />
                        <div>
                          <dt className="text-xs text-neutral-500">Harvest</dt>
                          <dd className="font-medium text-neutral-800 dark:text-neutral-200">
                            {custom.harvest}
                          </dd>
                        </div>
                      </div>
                    )}
                  </dl>
                </section>
              )}

              {/* Technical Notes */}
              {toggles.show_tasting_notes && ai?.technical_notes && (
                <section className="rounded-lg border p-5">
                  <h2 className="mb-2 text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                    Professional Notes
                  </h2>
                  <p className="text-sm text-neutral-600 dark:text-neutral-300">
                    {ai.technical_notes}
                  </p>
                </section>
              )}
            </div>
          </div>
        </main>

        {/* Footer */}
        <footer className="border-t px-4 py-6 text-center sm:px-6">
          <p className="text-xs text-neutral-400">
            Powered by{" "}
            <a
              href="https://catador.pro"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200"
            >
              Catador Pro
            </a>
          </p>
        </footer>
      </div>
    </>
  )
}
